import { Box, Button, Text, theme, useMediaQuery } from '@everlywell/ui-kit';
import AddToCartButton from 'components/AddToCartButton';
import { findContentfulFlagValue } from 'components/PageSections/utils';
import PriceV2 from 'components/PricingV2/PriceV2';
import PromoDiscountPrice from 'components/PricingV2/PromoDiscountPrice';
import { CardTags, CardTagsProps } from 'components/ProductDisplayGridV2';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { isEmpty } from 'lodash-es';
import React, { MouseEvent as RMouseEvent, MouseEventHandler } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import renderRichText from 'utils/contentful/renderRichText';
import { addSuperscript } from 'utils/helpers';
import {
  Promo,
  getActivePromo,
  promoAppliesToProduct,
} from 'utils/helpers/activePromoHelpers';
import { shouldUrlSkipScreenerModal } from 'utils/helpers/urls/skipModal';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ContentfulProductInfo, IGatsbyImage } from 'utils/types';
import { CallToAction, Media, Section } from 'utils/types/contentfulTypes';

import {
  trackAddToCartButtonClick,
  trackCardClick,
} from '../ProductCardWithScreener/productCardHelpers';
import { ProductCardV2Props } from './ProductCardV2';

type AddToCartCTAProps = {
  variantId: number;
  productId: number;
  displayName: string;
  productSlug: string;
  outOfStock?: boolean;
};

type TextContent = {
  content: string | React.ReactNode;
  color?: string;
};

type TitleAndDescriptionProps = {
  title: TextContent;
  description?: TextContent;
  hasLargeTitle?: boolean;
};

type StandardCardContent = {
  backgroundColor?: string;
  cta?: CallToAction;
  description?: string;
  images?: { desktopImage: IGatsbyImage; mobileImage: IGatsbyImage };
  linkTo?: string;
  textColor?: string;
  title?: string;
  note?: React.ReactNode;
  hasLargeTitle?: boolean;
};

type DefaultVariant = {
  product: Partial<ContentfulProductInfo>;
  promotionable: boolean;
  price?: string | null;
  variantId?: string;
  advertisedPromos?: Promo[];
  images: Media[];
};

type PricingOverrideProps = {
  promotionable: boolean;
  price?: number;
  activePromo?: Promo;
} & Pick<ContentfulProductInfo, 'productId' | 'outOfStock' | 'promoText'>;

type ProductCtaProps = {
  label?: string;
  url?: string;
  analyticsProps?: Record<string, string>;
};

const AddToCartCTA = ({
  displayName,
  productId,
  productSlug,
  variantId,
  outOfStock,
}: AddToCartCTAProps) => {
  const addToCartTrackingLabel = `Product Card - Add To Cart Button Click`;

  const handleAddToCart = () => {
    trackAddToCartButtonClick(productSlug, addToCartTrackingLabel);
  };

  return (
    <AddToCartButton
      product={{
        variantId,
        displayName,
        slug: productSlug,
        outOfStock,
      }}
      clickAnalyticsData={{
        label: addToCartTrackingLabel,
      }}
      onClick={handleAddToCart}
    />
  );
};

const TitleAndDescription = ({
  title,
  description,
  hasLargeTitle,
}: TitleAndDescriptionProps) => {
  const titleProps = hasLargeTitle
    ? {
        fontFamily: 'heading',
        fontSize: ['4xl', '6xl'],
        lineHeight: 'shorter',
        fontWeight: 'normal',
      }
    : {
        fontFamily: 'body',
        fontSize: ['lg', '3xl'],
        lineHeight: 'base',
        fontWeight: 'medium',
      };

  return (
    <>
      <Text {...titleProps} color={title?.color ?? 'black'} marginBottom={0.5}>
        {addSuperscript(String(title.content) ?? '')}
      </Text>
      {description?.content && (
        <Text
          fontSize="sm"
          color={description?.color ?? 'tints.darkGrey'}
          as={typeof description?.content !== 'string' ? 'div' : undefined}
        >
          {description.content}
        </Text>
      )}
    </>
  );
};

export const PricingOverride = ({
  productId,
  outOfStock,
  promotionable,
  promoText,
  price,
  activePromo,
}: PricingOverrideProps) => {
  if (!productId || !price) return null;

  const getPricingOverride = () => (
    <PromoDiscountPrice
      price={price}
      promo={activePromo}
      productId={productId}
      promotionable={promotionable}
      subscribeSelected={false}
      isTextReduced={true}
      isOutOfStock={outOfStock}
      promoText={activePromo ? undefined : promoText}
      displayCents={true}
    />
  );

  return activePromo ? getPricingOverride() : null;
};

const parseDefaultVariant = ({
  product,
  promotionable,
  price,
  variantId,
  advertisedPromos = [],
  images,
}: DefaultVariant) => {
  const activePromo = getActivePromo(advertisedPromos);
  const productHasPromo =
    product?.productId &&
    activePromo &&
    promoAppliesToProduct(product.productId, activePromo, promotionable);

  const mobileImage = images[0]?.mobileImage;
  const desktopImage = images[0]?.desktopImage;

  const image = (
    <>
      <Box display={['block', 'none']} height="100%">
        {mobileImage && (
          <GatsbyImage
            image={mobileImage.gatsbyImageData}
            // No alt text because the image is decorative and the product name is the card
            alt=""
            imgStyle={{ objectFit: 'contain' }}
            style={{ height: '100%' }}
          />
        )}
      </Box>
      <Box display={['none', 'block']} height="100%">
        {desktopImage && (
          <GatsbyImage
            image={desktopImage.gatsbyImageData}
            // No alt text because the image is decorative and the product name is the card
            alt=""
            imgStyle={{ objectFit: 'contain' }}
            style={{ height: '100%' }}
          />
        )}
      </Box>
    </>
  );

  const pricing = price && (
    <>
      <Text fontSize="lg" color="tints.black" fontWeight={'medium'} />
      <PriceV2 dollarAmount={(+price).toFixed(2)} />
    </>
  );

  const pricingOverride =
    product?.productId && price && advertisedPromos?.length > 0 ? (
      <PricingOverride
        productId={product?.productId}
        outOfStock={product?.outOfStock}
        promoText={product?.promoText}
        price={+price}
        promotionable={promotionable}
        activePromo={activePromo}
      />
    ) : null;

  const children = (
    <>
      <Box
        display={['flex', 'none']}
        gap="0.5"
        marginBottom={2}
        alignItems="center"
      >
        <CardTags
          product={product as unknown as CardTagsProps['product']}
          activePromoForProduct={productHasPromo ? activePromo : undefined}
        />
      </Box>
      <TitleAndDescription
        title={{ content: product?.displayName ?? '' }}
        description={{ content: product?.shortDescription ?? '' }}
      />
    </>
  );

  const cta = product?.productId && variantId && (
    <AddToCartCTA
      displayName={product?.displayName ?? ''}
      productId={product?.productId}
      productSlug={product?.slug ?? ''}
      variantId={Number(variantId)}
      outOfStock={product?.outOfStock}
    />
  );

  const tag = (
    <Box display={['none', 'flex']} gap="0.5" alignItems="center">
      <CardTags
        product={product as unknown as CardTagsProps['product']}
        activePromoForProduct={productHasPromo ? activePromo : undefined}
      />
    </Box>
  );

  const handleCardClick =
    (productSlug: string | undefined): MouseEventHandler<HTMLAnchorElement> =>
    () => {
      if (!productSlug) return;

      trackCardClick(productSlug, 'Product Card - Card Click');
    };

  return {
    image,
    pricing,
    pricingOverride,
    children,
    cta,
    tag,
    handleOnClick: handleCardClick(product?.slug),
    variant: 'default',
    linkTo: `/products/${product?.slug}/`,
  };
};

const parseStandardVariant = (cardContent: StandardCardContent) => {
  const {
    title,
    backgroundColor,
    textColor,
    images,
    cta,
    description,
    linkTo,
    note,
    hasLargeTitle,
  } = cardContent;
  const ctaColorScheme = cta?.metadata?.context?.cta_color_scheme;

  const mobileImageData = images?.mobileImage.gatsbyImageData;
  const desktopImageData = images?.desktopImage.gatsbyImageData;

  const handleOnClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_PROMO_CARD,
      data: {
        label: title,
        slug: linkTo,
      },
    });
  };

  const ProductImage = () => {
    const [isMobile] = useMediaQuery(
      `(max-width: ${theme.breakpoints.tablet})`,
    );
    let imageData =
      isMobile && mobileImageData ? mobileImageData : desktopImageData;

    if (!imageData) return null;

    return (
      <GatsbyImage
        image={imageData}
        alt={`${title} image`}
        imgStyle={{ objectFit: 'contain' }}
      />
    );
  };
  const ProductCta = ({ label, url, analyticsProps = {} }: ProductCtaProps) => {
    const { handleMdpCtaClick } = useMdpModals();

    const trackCTA = (): void => {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_LINK,
        data: {
          label: label,
          url: url,
          ...analyticsProps,
        },
      });
    };

    const modalHandler = (
      event: RMouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      // Keeps compatibility with the disclaimer modal
      const skipScreenerModal = shouldUrlSkipScreenerModal({ url });
      handleMdpCtaClick({
        event,
        ctaUrl: url || '',
        skipScreenerModal,
      });
    };

    const handleCTAClick = (
      event: RMouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      modalHandler(event);
      trackCTA();
    };

    if (isEmpty(url) || isEmpty(label)) {
      return null;
    }

    return (
      <Button
        size="sm"
        colorScheme={ctaColorScheme}
        onClick={handleCTAClick}
        {...(url?.includes('#disclaimer') ? {} : { as: Link, to: url })}
      >
        {label}
      </Button>
    );
  };

  return {
    cardBackgroundColor: backgroundColor,
    children: title && (
      <TitleAndDescription
        title={{ content: title, color: textColor }}
        description={{ content: note }}
        hasLargeTitle={hasLargeTitle}
      />
    ),
    cta: cta && (
      <ProductCta
        analyticsProps={cta?.metadata?.context?.mixpanel_props}
        label={cta?.label}
        url={cta?.url}
      />
    ),
    handleOnClick,
    image: <ProductImage />,
    linkTo,
    pricing: description && <Text size="sm">{description}</Text>,
    variant: 'standard',
  };
};

export const parseProductCardV2 = (
  card: Section,
  advertisedPromos: Promo[],
): ProductCardV2Props => {
  const cardVariant =
    card?.metadata?.context?.product_card_variant ?? 'default';
  const subsection = card?.subsections?.[0];

  switch (cardVariant) {
    case 'standard': {
      const images = subsection?.images;
      const { title, theme, ctas, description, flags } =
        subsection?.metadata ?? {};
      const cta = ctas?.[0];
      const linkTo = ctas?.[1]?.url;
      const { backgroundColor, textColor } = theme ?? {};

      const hasLargeTitle = findContentfulFlagValue(
        'hasLargeTitle',
        flags as Queries.ContentfulFlag001[],
      );

      const cardContent = {
        backgroundColor,
        cta,
        description: description?.description,
        images: images?.[0],
        textColor,
        title,
        linkTo,
        hasLargeTitle,
        note: subsection?.body ? renderRichText(subsection?.body) : '',
      };

      return parseStandardVariant(cardContent) as ProductCardV2Props;
    }
    default: {
      const variant = subsection?.spreeProduct?.variants?.find(
        (variant) => variant?.is_master === true,
      );

      const price = variant?.price;
      const variantId = variant?.id;

      const product = subsection?.spreeProduct?.contentfulProductInfo!;
      const promotionable = subsection?.spreeProduct?.promotionable ?? false;

      const images = subsection?.images ?? [];

      return parseDefaultVariant({
        product,
        promotionable,
        price,
        variantId,
        advertisedPromos,
        images,
      }) as ProductCardV2Props;
    }
  }
};
